import type { ReactNode } from "react";
import { cn } from "@/utils/helpers";
import { BulletList, BriefSection, SectionHead } from "./BriefPrimitives";

/** One block of the 420 column: a 13 muted label and a 15 value. */
function SideBlock({ label, value }: { label: string; value?: string | null }) {
  if (!value?.trim()) return null;
  return (
    <div>
      <p className="text-[13px] text-co-muted">{label}</p>
      <p className="mt-1.5 whitespace-pre-wrap text-[15px] leading-[1.5] text-co-text2">{value}</p>
    </div>
  );
}

/**
 * The treatment frame: the lead on the left, a 420 column behind a hairline on
 * the right (300 on a tablet). Under 36rem of brief width (phones) the column
 * goes under the lead behind a top hairline.
 */
function TreatmentFrame({ lead, side }: { lead: ReactNode; side: ReactNode[] }) {
  const hasSide = side.some(Boolean);
  return (
    <BriefSection accent="blue">
      <SectionHead accent="blue" title="Treatment" />
      <div className={cn("grid gap-[18px] @[36rem]/briefdoc:gap-10", hasSide && "@[36rem]/briefdoc:grid-cols-[minmax(0,1fr)_300px] @[52rem]/briefdoc:grid-cols-[minmax(0,1fr)_420px]")}>
        <div className="min-w-0">{lead}</div>
        {hasSide ? (
          <div className="flex min-w-0 flex-col gap-[18px] border-t border-co-line pt-[18px] @[36rem]/briefdoc:border-l @[36rem]/briefdoc:border-t-0 @[36rem]/briefdoc:py-0.5 @[36rem]/briefdoc:pl-8">
            {side}
          </div>
        ) : null}
      </div>
    </BriefSection>
  );
}

export function BriefTreatment({
  hookVo,
  conceptCues,
  soundDirection,
  length,
  ctaTextOnScreen,
}: {
  hookVo?: string;
  conceptCues?: string[];
  soundDirection?: string;
  /** Built by lengthSentence(); null hides the block. */
  length?: string | null;
  ctaTextOnScreen?: string;
}) {
  const hook = hookVo?.trim();
  const cues = (conceptCues ?? []).map((cue) => cue?.trim()).filter((cue): cue is string => !!cue);
  const cta = ctaTextOnScreen?.trim();
  if (!hook && !cues.length && !soundDirection?.trim() && !length && !cta) return null;
  return (
    <TreatmentFrame
      lead={
        hook || cues.length ? (
          <>
            {hook ? (
              <>
                <p className="text-[13px] text-co-muted">Opening hook</p>
                <p className="mt-2.5 whitespace-pre-wrap font-section text-[30px] font-bold leading-[1.15] text-co-text">
                  {hook}
                </p>
              </>
            ) : null}
            {cues.length ? (
              <div className={hook ? "mt-6" : undefined}>
                <p className="text-[13px] text-co-muted">Cues</p>
                <BulletList items={cues} className="mt-2.5" />
              </div>
            ) : null}
          </>
        ) : null
      }
      side={[
        soundDirection?.trim() ? <SideBlock key="sound" label="Sound" value={soundDirection} /> : null,
        length ? <SideBlock key="length" label="Length" value={length} /> : null,
        cta ? <SideBlock key="cta" label="Call to action on screen" value={cta} /> : null,
      ]}
    />
  );
}

export function BriefTreatmentStatic({
  headline,
  format,
  size,
  ctaTextOnArtwork,
}: {
  headline?: string;
  format?: string | null;
  size?: string | null;
  ctaTextOnArtwork?: string;
}) {
  const text = headline?.trim();
  const cta = ctaTextOnArtwork?.trim();
  if (!text && !format && !size && !cta) return null;
  return (
    <TreatmentFrame
      lead={
        text ? (
          <>
            <p className="text-[13px] text-co-muted">Headline on the artwork</p>
            <p className="mt-2.5 whitespace-pre-wrap font-section text-[30px] font-bold leading-[1.15] text-co-text">
              {text}
            </p>
          </>
        ) : null
      }
      side={[
        format ? <SideBlock key="format" label="Format" value={format} /> : null,
        size ? <SideBlock key="size" label="Size" value={size} /> : null,
        cta ? <SideBlock key="cta" label="Call to action on the artwork" value={cta} /> : null,
      ]}
    />
  );
}
